<template>
  <o-table
    paginated
    :default-sort="['createdAt', 'desc']"
    :items="taskGroups"
    :loading="loading"
    :detailed="true"
    :draggable="false"
  >
    <b-table-column v-slot="{ row }" field="status" label="Statut" sortable>
      <div class="is-flex is-align-items-center">
        <b-icon :icon="row.icon" :type="row.type" size="is-small" class="mr-1" />
        <b-tag :type="row.type">
          {{ row.tag }}
        </b-tag>
      </div>
    </b-table-column>
    <b-table-column v-slot="{ row }" field="administratorId" label="Admin" sortable>
      {{ administratorNamesById[row.administratorId] }}
    </b-table-column>
    <b-table-column v-slot="{ row }" field="createdAt" label="Date de création" sortable>
      {{ formatDate(row.createdAt) }}
    </b-table-column>
    <b-table-column v-slot="{ row, toggleDetails }" width="100">
      <div class="buttons are-small is-right">
        <b-tooltip position="is-left" multilined type="is-light">
          <b-button type="is-primary" @click="openRow(row, toggleDetails)">
            <b-icon :icon="row.id === openedRow?.id ? 'eye-slash' : 'eye'" />
          </b-button>
          <template v-slot:content> Voir les transferts </template>
        </b-tooltip>
      </div>
    </b-table-column>
    <template #detail="{ row, toggleDetails }">
      <div v-if="row.pending" class="columns is-multiline is-vcentered">
        <div class="column is-full-mobile">
          <b-message :type="message.type" size="is-small">
            {{ message.message }}
          </b-message>
        </div>
        <div class="column is-narrow is-full-mobile">
          <b-button
            :disabled="loading || hasIncompleteTasks"
            type="is-primary"
            :loading="loading"
            @click="completeTaskGroup(row, toggleDetails)"
          >
            Cloturer les transferts
          </b-button>
        </div>
      </div>
      <spot-tracker-subscription-task-table
        :disable-status-update="!row.pending"
        :spot-tracker-subscription-task-group="row"
        :spot-trackers="spotTrackerNumberById"
        @completed="hasIncompleteTasks = !$event"
      />
    </template>
  </o-table>
</template>

<script>
import SpotTrackerSubscriptionTaskTable from './SpotTrackerSubscriptionTaskTable.vue'
import OTable from '@components/Table.vue'
import { mapActions } from 'vuex'
import { format } from 'date-fns'

export const SPOT_TRACKER_SUBSCRIPTION_TASK_GROUP_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
}

export const SPOT_TRACKER_SUBSCRIPTION_TASK_GROUP_STATUS_CONFIG = {
  [SPOT_TRACKER_SUBSCRIPTION_TASK_GROUP_STATUS.PENDING]: {
    tag: 'En attente',
    type: 'is-primary',
    icon: 'hourglass-end',
  },
  [SPOT_TRACKER_SUBSCRIPTION_TASK_GROUP_STATUS.COMPLETED]: {
    tag: 'Terminé',
    type: 'is-success',
    icon: 'check',
  },
}

export default {
  name: 'SpotTrackerSubscriptionTaskGroupTable',

  components: {
    SpotTrackerSubscriptionTaskTable,
    OTable,
  },

  props: {
    spotTrackers: {
      type: Array,
      required: true,
    },
    spotTrackerSubscriptionTaskGroups: {
      type: Array,
      required: true,
    },
    administrators: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      openedRow: null,
      hasIncompleteTasks: true,
    }
  },

  computed: {
    administratorNamesById() {
      return this.administrators.reduce((administrators, administrator) => {
        administrators[administrator.id] = `${administrator.firstname} ${administrator.lastname}`

        return administrators
      }, {})
    },

    spotTrackerNumberById() {
      return this.spotTrackers.reduce((spotTrackers, spotTracker) => {
        spotTrackers[spotTracker.id] = spotTracker.number

        return spotTrackers
      }, {})
    },

    taskGroups() {
      return this.spotTrackerSubscriptionTaskGroups.map((group) => {
        const config = SPOT_TRACKER_SUBSCRIPTION_TASK_GROUP_STATUS_CONFIG[group.status] ?? {
          tag: group.status,
          type: 'is-light',
          icon: 'question',
        }

        return {
          ...group,
          ...config,
          pending: group.status === SPOT_TRACKER_SUBSCRIPTION_TASK_GROUP_STATUS.PENDING,
        }
      })
    },

    message() {
      if (this.hasIncompleteTasks) {
        return { type: 'is-warning', message: 'Des transferts sont en cours ou non effectués.' }
      }
      return { type: 'is-success', message: 'Tous les transferts ont été effectués.' }
    },
  },

  methods: {
    ...mapActions('ui', ['addToastMessage']),

    formatDate(date) {
      return format(date, 'dd/MM/yyyy')
    },

    openRow(row, toggleDetails) {
      if (this.openedRow && this.openedRow.id !== row.id) {
        toggleDetails(this.openedRow)
      }

      toggleDetails(row)
      if (this.openedRow?.id === row.id) {
        this.openedRow = null

        return
      }

      this.openedRow = row
      this.hasIncompleteTasks = true
    },

    async completeTaskGroup(taskGroup, toggleDetails) {
      this.loading = true

      try {
        const completedTaskGroup =
          await this.$services.spotTrackerSubscriptionTaskGroupService.complete(taskGroup.id)

        this.$emit(
          'update:spotTrackerSubscriptionTaskGroups',
          this.spotTrackerSubscriptionTaskGroups.map((group) =>
            group.id === completedTaskGroup.id ? completedTaskGroup : group,
          ),
        )

        toggleDetails(taskGroup)
        this.openedRow = null

        this.addToastMessage({
          text: `Le groupe de transfert est clôturé.`,
          type: 'is-success',
        })
      } catch (err) {
        this.addToastMessage({
          text: err.message ?? `Impossible de clôturer le groupe.`,
          type: 'is-danger',
        })
        console.error(err)
      }

      this.loading = false
    },
  },
}
</script>
